import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { formatDateTime } from '../../modules/datetime/utils'
import { DATE_FORMAT_HUMAN } from '../../modules/datetime/constants'
import { PageHeader } from '../PageHeader'
import { MdInbox } from 'react-icons/md'
import { DotsLoading } from '../Loading'
import { CollapsibleSection } from './CollapsibleSection'
import { FiveColumnResultCards } from './FiveColumnResultCards'
import { ResultTable, ResultRow } from './ResultTable'
import {
  type YeekeeRoundRow,
  type DateGroup,
  type DailyDataTypes,
} from './ResultHistory.types'
import { useCurrentEvent, EventType } from '../../hooks/useCurrentEvent'
import { useThemeAssets } from '../../hooks/useThemeAssets'

const isStock = (t?: string) => t === 'stock'
const isGovernment = (t?: string) => t === 'default'
const isGroupFour = (t?: string) => t === 'group'
const isGroupSix = (t?: string) => t === 'group_six'

export interface ResultHistoryViewProps {
  t: TFunction
  loading: boolean
  gameName: string
  gameType: string
  onBack: () => void
  collapsedMap: Record<string, boolean>
  toggleCollapsed: (key: string) => void
  hasRoundBasedLayout: boolean
  hasAnyLayout: boolean
  hasFilteredData: boolean
  dailyData: DailyDataTypes[]
  roundBasedData: DateGroup<YeekeeRoundRow>[]
}

export function ResultHistoryView({
  t,
  loading,
  gameName,
  gameType,
  onBack,
  collapsedMap,
  toggleCollapsed,
  hasRoundBasedLayout,
  hasAnyLayout,
  hasFilteredData,
  dailyData,
  roundBasedData,
}: ResultHistoryViewProps) {
  const { i18n } = useTranslation()
  const event = useCurrentEvent()
  const { svgAssets } = useThemeAssets()

  const isHalloween = event === EventType.HALLOWEEN
  const isLoyKrathong = event === EventType.LOY_KRATHONG
  const abstractBorderImage = (svgAssets as { ABSTRACT_BORDER?: string | null })
    ?.ABSTRACT_BORDER
  const skullImage = isHalloween ? abstractBorderImage : null
  const palaceDecorImage = isLoyKrathong ? abstractBorderImage : null

  return (
    <div className="w-full max-w-7xl mx-auto pb-6 sm:pb-8">
      <PageHeader title={t('resultPage.resultHistoryTitle')} onBack={onBack} />

      <div className="mt-6 rounded-xl border border-[var(--card-divider)] bg-[var(--card-bg)] p-5 sm:p-6 shadow-sm relative overflow-visible">
        {/* Halloween Skull Decoration - Right side */}
        {isHalloween && skullImage && (
          <img
            src={skullImage}
            alt="Halloween Skull"
            className="halloween-result-skull"
          />
        )}
        {/* Loy Krathong Palace Decoration - Right side */}
        {isLoyKrathong && palaceDecorImage && (
          <img
            src={palaceDecorImage}
            alt="Loy Krathong Palace"
            className="loy-krathong-result-palace"
          />
        )}
        <div className="flex flex-col">
          <div
            className="text-xl sm:text-2xl font-semibold text-[var(--heading-text)]"
            role="heading"
            aria-level={2}
          >
            {gameName || t('resultPage.resultHistoryTitle')}
          </div>
        </div>
      </div>

      {!gameType && (
        <div className="mt-8 rounded-xl border border-[var(--card-divider)] bg-[var(--card-bg)] p-8 text-center shadow-sm">
          <MdInbox className="mx-auto text-4xl text-[var(--color-primary-400)] mb-3" />
          <p className="text-[var(--heading-text)] font-medium">
            {t('resultPage.noHistoryData')}
          </p>
        </div>
      )}

      {loading && (
        <div className="mt-8 flex justify-center">
          <DotsLoading size="md" />
        </div>
      )}

      {!loading && gameType && !hasAnyLayout && (
        <div className="mt-8 rounded-xl border border-[var(--card-divider)] bg-[var(--card-bg)] p-8 text-center shadow-sm">
          <MdInbox className="mx-auto text-4xl text-[var(--color-primary-400)] mb-3" />
          <p className="text-[var(--heading-text)] font-medium">
            {t('resultPage.noHistoryData')}
          </p>
        </div>
      )}

      {!loading && gameType && hasAnyLayout && !hasFilteredData && (
        <div className="mt-8 rounded-xl border border-[var(--card-divider)] bg-[var(--card-bg)] p-8 text-center shadow-sm">
          <MdInbox className="mx-auto text-4xl text-[var(--color-primary-400)] mb-3" />
          <p className="text-[var(--heading-text)] font-medium">
            {t('resultPage.noHistoryData')}
          </p>
        </div>
      )}

      {/* Yeekee / Yeekee Malta */}
      {!loading && hasRoundBasedLayout && roundBasedData.length > 0 && (
        <div className="mt-8 space-y-4" data-kind="yeekee">
          {roundBasedData.map((dayGroup) => {
            const dateKey = dayGroup.date
            return (
              <CollapsibleSection
                key={dateKey}
                id={`yeekee-${dateKey}`}
                title={formatDateTime(
                  DATE_FORMAT_HUMAN,
                  dateKey,
                  i18n.language,
                )}
                subtitle={t('resultPage.roundsCount', {
                  count: dayGroup.rounds.length,
                })}
                collapsed={collapsedMap[dateKey] ?? true}
                onToggle={() => toggleCollapsed(dateKey)}
              >
                <ResultTable
                  headers={[
                    t('resultPage.columnRound'),
                    t('resultPage.columnThreeTop'),
                    t('resultPage.columnTwoUnder'),
                  ]}
                >
                  {dayGroup.rounds.map((row, idx) => (
                    <ResultRow key={row.roundNumber} idx={idx}>
                      <td className="px-4 py-3 text-base font-normal text-[var(--heading-text)] text-center">
                        {row.roundNumber}
                      </td>
                      <td className="px-4 py-3 text-base font-bold tracking-[0.16em] text-[var(--heading-text)] text-center">
                        {row.threeTop}
                      </td>
                      <td className="px-4 py-3 text-base font-bold tracking-[0.16em] text-[var(--heading-text)] text-center">
                        {row.twoUnder}
                      </td>
                    </ResultRow>
                  ))}
                </ResultTable>
              </CollapsibleSection>
            )
          })}
        </div>
      )}

      {/* Government: unique card layout */}
      {!loading &&
        isGovernment(gameType) &&
        dailyData.length > 0 &&
        dailyData.every((row) => row.kind === 'government') && (
          <div className="mt-8" data-kind="government">
            <FiveColumnResultCards rows={dailyData} t={t} />
          </div>
        )}

      {/* Stock / International */}
      {!loading &&
        isStock(gameType) &&
        dailyData.length > 0 &&
        dailyData.every((row) => row.kind === 'stock') && (
          <div className="mt-8" data-kind="stock">
            <CollapsibleSection
              id="stock"
              title={gameName || t('resultPage.resultHistoryTitle')}
              subtitle={t('resultPage.roundsCount', {
                count: dailyData.length,
              })}
              collapsed={collapsedMap['stock'] ?? false}
              onToggle={() => toggleCollapsed('stock')}
            >
              <ResultTable
                headers={[
                  t('resultPage.columnDate'),
                  t('resultPage.columnThreeTop'),
                  t('resultPage.columnTwoUnder'),
                ]}
              >
                {dailyData.map((row, idx) => (
                  <ResultRow key={row.date} idx={idx}>
                    <td className="px-4 py-3 text-base font-normal text-[var(--heading-text)] text-center">
                      {formatDateTime(
                        DATE_FORMAT_HUMAN,
                        row.date,
                        i18n.language,
                      )}
                    </td>
                    <td className="px-4 py-3 text-base font-bold text-[var(--heading-text)] text-center">
                      {row.threeTop}
                    </td>
                    <td className="px-4 py-3 text-base font-bold text-[var(--heading-text)] text-center">
                      {row.twoUnder}
                    </td>
                  </ResultRow>
                ))}
              </ResultTable>
            </CollapsibleSection>
          </div>
        )}

      {/* Group 4-digit */}
      {!loading &&
        isGroupFour(gameType) &&
        dailyData.length > 0 &&
        dailyData.every((row) => row.kind === 'group') && (
          <div className="mt-8" data-kind="group">
            <CollapsibleSection
              id="group4"
              title={gameName || t('resultPage.resultHistoryTitle')}
              subtitle={t('resultPage.roundsCount', {
                count: dailyData.length,
              })}
              collapsed={collapsedMap['group4'] ?? false}
              onToggle={() => toggleCollapsed('group4')}
            >
              <ResultTable
                headers={[
                  t('resultPage.columnDate'),
                  t('resultPage.columnFourResult'),
                ]}
              >
                {dailyData.map((row, idx) => (
                  <ResultRow key={row.date} idx={idx}>
                    <td className="px-4 py-3 text-base font-normal text-[var(--heading-text)] text-center">
                      {formatDateTime(
                        DATE_FORMAT_HUMAN,
                        row.date,
                        i18n.language,
                      )}
                    </td>
                    <td className="px-4 py-3 text-base font-bold tracking-[0.12em] text-[var(--heading-text)] text-center">
                      {row.fourDigit}
                    </td>
                  </ResultRow>
                ))}
              </ResultTable>
            </CollapsibleSection>
          </div>
        )}

      {/* Group 6-digit */}
      {!loading &&
        isGroupSix(gameType) &&
        dailyData.length > 0 &&
        dailyData.every((row) => row.kind === 'group_six') && (
          <div className="mt-8" data-kind="group_six">
            <CollapsibleSection
              id="group6"
              title={gameName || t('resultPage.resultHistoryTitle')}
              subtitle={t('resultPage.roundsCount', {
                count: dailyData.length,
              })}
              collapsed={collapsedMap['group6'] ?? false}
              onToggle={() => toggleCollapsed('group6')}
            >
              <ResultTable
                headers={[
                  t('resultPage.columnDate'),
                  t('resultPage.columnSixResult'),
                ]}
              >
                {dailyData.map((row, idx) => (
                  <ResultRow key={row.date} idx={idx}>
                    <td className="px-4 py-3 text-base font-normal text-[var(--heading-text)] text-center">
                      {formatDateTime(
                        DATE_FORMAT_HUMAN,
                        row.date,
                        i18n.language,
                      )}
                    </td>
                    <td className="px-4 py-3 text-base font-bold tracking-[0.12em] text-[var(--heading-text)] text-center">
                      {row.sixDigit}
                    </td>
                  </ResultRow>
                ))}
              </ResultTable>
            </CollapsibleSection>
          </div>
        )}
    </div>
  )
}
